import { AuthDTO } from "@/types/api/auth";
import axios, { AxiosError, AxiosHeaders, InternalAxiosRequestConfig } from "axios";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || "http://127.0.0.1:8000/api"

// Store for Clerk token getter
let getClerkToken: (() => Promise<string | null>) | null = null;

export const setClerkTokenGetter = (getter: () => Promise<string | null>) => {
    getClerkToken = getter;
};

const readAuthFromStorage = (): { user: any; token: string } | null => {
    if (typeof window === "undefined") return null;

    // Check "auth" first (Clerk JWT) before "ace_auth" (old Sanctum token)
    const storageKeys = ["auth", "ace_auth"];

    for (const key of storageKeys) {
        try {
            const raw = window.localStorage.getItem(key);
            if (!raw) {
                continue;
            }

            const parsed = JSON.parse(raw) as { user?: any; token?: string; auth?: { user?: any; token?: string } };
            const token = parsed.token ?? parsed.auth?.token;
            const user = parsed.user ?? parsed.auth?.user;

            if (token) {
                return { user, token };
            }
        } catch (error) {
            console.warn(`Failed to parse auth payload from localStorage key "${key}"`, error);
        }
    }

    return null;
};

const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 60000,
    headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
    },
});

instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const headers = AxiosHeaders.from(config.headers ?? {});

    // Try to get Clerk token first
    if (getClerkToken) {
        try {
            const clerkToken = await getClerkToken();
            if (clerkToken) {
                headers.set("Authorization", `Bearer ${clerkToken}`);
                config.headers = headers;
                return config;
            }
        } catch (error) {
            console.warn("Failed to get Clerk token", error);
        }
    }

    // Fallback to localStorage auth
    const auth = readAuthFromStorage();
    if (auth?.token) {
        headers.set("Authorization", `Bearer ${auth.token}`);
    } else {
        headers.delete("Authorization");
    }

    config.headers = headers;
    return config;
});

instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // On 401, retry once with a fresh Clerk token (handles race condition
        // where the initial request used a stale/null token because Clerk
        // session hadn't loaded yet).
        if (
            error.response?.status === 401 &&
            getClerkToken &&
            originalRequest &&
            !originalRequest._retry
        ) {
            originalRequest._retry = true;
            try {
                const freshToken = await getClerkToken();
                if (freshToken) {
                    const headers = AxiosHeaders.from(originalRequest.headers ?? {});
                    headers.set("Authorization", `Bearer ${freshToken}`);
                    originalRequest.headers = headers;
                    return instance.request(originalRequest);
                }
            } catch (retryError) {
                console.warn("Token refresh retry failed:", retryError);
            }
        }

        console.log(error);
        return Promise.reject(error);
    }
);

export default instance;
